import * as React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useT } from "talkr";
import { Button, Fab, IconButton, Link } from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import Grid from "@mui/material/Grid2";
import Section from "./Section";

import couple from "./assets/sportsmanias-just-married.gif";
import { useRef, useMemo, useEffect, useState } from "react";
// import json from i18n/en.json
import en from "./i18n/en.json";
import location from "./assets/location.gif";
import FAQAccordion from "./Accordion";
import { BodyTypography } from "./typography/Typography";
import PlaceIcon from "@mui/icons-material/Place";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
//@ts-ignore
import Flag from "react-world-flags";
import Markdown from "react-markdown";
import ColoredSVG from "./Colorpalette";
import { myColors } from "./theme";
import CustomizedTimeline from "./Timeline";
// Local background image from assets
import Background from "./assets/Background_blue.png";
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import DressCodeMosaic from './DressCodeMosaic';
import MultiCarousel from "./MultiCarousel";
import matejAndYun from "./assets/Matej_and_Yun.png";
import bouquet from "./assets/bouquet.png";
import { alpha } from '@mui/material/styles';

import photo01 from "./assets/gallery/image1.jpg";
import photo02 from "./assets/gallery/image2.jpg";
import photo03 from "./assets/gallery/image3.jpg";
import photo04 from "./assets/gallery/image4.jpg";
import photo05 from "./assets/gallery/image5.jpg";
import photo06 from "./assets/gallery/image6.jpg";
import photo07 from "./assets/gallery/image7.jpg";

// SoundCloud Widget API types
interface SoundCloudWidget {
  play: () => void;
  pause: () => void;
}

interface WindowWithSC extends Window {
  SC?: {
    Widget: (iframe: HTMLIFrameElement) => SoundCloudWidget;
  };
}

export default function App() {
  const { T, setLocale } = useT();
  const [playerPlaying, setPlayerPlaying] = React.useState(false);
  const [colorPalette, setColorPalette] = React.useState<string[]>([]);
  const scWidgetRef = useRef<SoundCloudWidget | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Initialize SoundCloud Widget API and handle play/pause
  React.useEffect(() => {
    // Check if script already exists to avoid duplicates
    const existingScript = document.querySelector('script[src="https://w.soundcloud.com/player/api.js"]');
    if (existingScript) {
      // Script already loaded, initialize widget directly
      const windowWithSC = window as unknown as WindowWithSC;
      if (iframeRef.current && windowWithSC.SC) {
        scWidgetRef.current = windowWithSC.SC.Widget(iframeRef.current);
      }
      return;
    }

    // Load SoundCloud Widget API script
    const script = document.createElement('script');
    script.src = 'https://w.soundcloud.com/player/api.js';
    script.async = true;
    script.onload = () => {
      const windowWithSC = window as unknown as WindowWithSC;
      if (iframeRef.current && windowWithSC.SC) {
        scWidgetRef.current = windowWithSC.SC.Widget(iframeRef.current);
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const scriptToRemove = document.querySelector('script[src="https://w.soundcloud.com/player/api.js"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Control playback based on playerPlaying state
  React.useEffect(() => {
    if (scWidgetRef.current) {
      if (playerPlaying) {
        scWidgetRef.current.play();
      } else {
        scWidgetRef.current.pause();
      }
    }
  }, [playerPlaying]);

  const generateDressCodePalette = () => {
    const colors = myColors.dressCodepalette;
    const palette = Array.from({ length: 13 }, (_, i) => colors[i % colors.length]);
    return palette.sort(() => Math.random() - 0.5);
  };

  React.useEffect(() => {
    const palette = generateDressCodePalette();
    setColorPalette(palette);
  }, []);

  const updatePalette = () => {
    const palette = generateDressCodePalette();
    setColorPalette(palette);
  };

  React.useEffect(() => {
    const interval = setInterval(updatePalette, 2000);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        clearInterval(interval);
        updatePalette();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      clearInterval(interval);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  type SectionType = {
    title: string;
    innerRef: React.RefObject<any>;
    color: string;
    backgroundColor: string;
  };

  const heroRef = useRef<HTMLElement | null>(null);
  const detailsRef = useRef<HTMLElement | null>(null);
  const programRef = useRef<HTMLElement | null>(null);
  const locationRef = useRef<HTMLElement | null>(null);
  const dressCodeRef = useRef<HTMLElement | null>(null);
  const qnaRef = useRef<HTMLElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);

  const sections: { [key: string]: SectionType } = {
    details: {
      title: T("details"),
      color: "secondary.main",
      backgroundColor: "secondary.light",
      innerRef: detailsRef,
    },
    program: {
      title: T("program"),
      color: "primary.contrastText",
      backgroundColor: "background.default",
      innerRef: programRef,
    },
    location: {
      title: T("location.title"),
      color: "secondary.main",
      backgroundColor: "secondary.light",
      innerRef: locationRef,
    },
    dressCode: {
      title: T("dressCode"),
      color: "primary.contrastText",
      backgroundColor: "background.default",
      innerRef: dressCodeRef,
    },
    questionsAndAnswers: {
      title: T("questionsAndAnswersTitle"),
      color: "secondary.main",
      backgroundColor: "secondary.light",
      innerRef: qnaRef,
    },
  };

  const NavBarButtons = () => (
    <>
      {Object.keys(sections).map((section) => (
        <Button
          key={section}
          sx={{
            p: { xs: 0.5, sm: 1 },
            textAlign: "center",
            //margin: { xs: 0.5, sm: 1 },
            whiteSpace: "nowrap",
            minWidth: "0",
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
               flexShrink: 0,
          }}
          onClick={() =>
            sections[section].innerRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            })
          }
        >
          {sections[section].title}
        </Button>
      ))}
    </>
  );

  const flagButtons = () => {
    const buttonSx = { 
      p: { xs: 0.2, sm: 0.5, md: 1 }, 
      textAlign: "center", 
      margin: { xs: 0.2, sm: 0.5, md: 1 }, 
      color: "primary.contrastText", 
      whiteSpace: "nowrap", 
      minWidth: "0",
      width: { xs: 28, sm: 40, md: 48 },
      height: { xs: 28, sm: 40, md: 48 },
        flexShrink: 0, 
    };
    
    const flagStyle = { 
      width: '100%', 
      height: '100%',
      objectFit: 'contain' as const
    };
    
    return (
      <>
        <IconButton sx={buttonSx} onClick={() => setLocale("en")}>
          <Flag code="GB" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("tw")}>
          <Flag code="TW" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("sk")}>
          <Flag code="sk" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("cs")}>
          <Flag code="CZ" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("es")}>
          <Flag code="CO" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("it")}>
          <Flag code="IT" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("el")}>
          <Flag code="GR" style={flagStyle} />
        </IconButton>
        <IconButton sx={buttonSx} onClick={() => setLocale("tr")}>
          <Flag code="TR" style={flagStyle} />
        </IconButton>
      </>
    );
  };

  const usPhotos = [
    photo01,
    photo02,
    photo03,
    photo04,
    photo05,
    photo06,
    photo07,
  ];

  // main container ref and throttle
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isThrottled = useRef(false);

  // disable snap while interacting with flags or overlay is open
  const disableSnapRef = useRef(false);
  const disableSnapTimeoutRef = useRef<number | null>(null);
  const setDisableSnapTemporarily = (ms = 2000) => {
    disableSnapRef.current = true;
    if (disableSnapTimeoutRef.current) {
      window.clearTimeout(disableSnapTimeoutRef.current);
    }
    disableSnapTimeoutRef.current = window.setTimeout(() => {
      disableSnapRef.current = false;
      disableSnapTimeoutRef.current = null;
    }, ms);
  };

  const orderedRefs = useMemo(
    () => [heroRef, detailsRef, programRef, locationRef, dressCodeRef, qnaRef, footerRef],
    []
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const wheelHandler = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;

      if (disableSnapRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const positions = orderedRefs.map((r) => {
        const el = r.current;
        if (!el) return Number.POSITIVE_INFINITY;
        const rect = el.getBoundingClientRect();
        return rect.top - containerRect.top;
      });

      let currentIndex = 0;
      let minAbs = Infinity;
      positions.forEach((p, i) => {
        const absP = Math.abs(p);
        if (absP < minAbs) {
          minAbs = absP;
          currentIndex = i;
        }
      });

      const currentEl = orderedRefs[currentIndex].current;
      if (!currentEl) return;

      const elRect = currentEl.getBoundingClientRect();
      const threshold = 4;

      if (e.deltaY > 0) {
        const hasMoreBelow = elRect.bottom > containerRect.bottom + threshold;
        if (hasMoreBelow) {
          return;
        }
      } else {
        const hasMoreAbove = elRect.top < containerRect.top - threshold;
        if (hasMoreAbove) {
          return;
        }
      }

      e.preventDefault();
      if (isThrottled.current) return;

      const direction = e.deltaY > 0 ? 1 : -1;
      let targetIndex = currentIndex + direction;
      if (targetIndex < 0) targetIndex = 0;
      if (targetIndex > orderedRefs.length - 1) targetIndex = orderedRefs.length - 1;

      const target = orderedRefs[targetIndex].current;
      if (!target) return;

      isThrottled.current = true;

      const inHero = currentIndex === 0;
      const baseBuffer = Math.round(Math.min(window.innerHeight * 0.12, 180));
      const bufferPx = inHero ? Math.round(Math.min(window.innerHeight * 0.40, 600)) : baseBuffer;

      container.scrollBy({ top: bufferPx * direction, behavior: "smooth" });

      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          isThrottled.current = false;
        }, 700);
      }, 450);
    };

    container.addEventListener("wheel", wheelHandler as any, { passive: false });
    return () => {
      container.removeEventListener("wheel", wheelHandler as any);
    };
  }, [orderedRefs]);

  return (
    <Box
      id="scroll-container"
      ref={containerRef}
      sx={{
        height: '100vh',
        overflowY: 'auto',
        scrollSnapType: 'y mandatory',
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth',
        overscrollBehavior: 'none',
      }}
    >
      <GlobalStyles
        styles={{
          body: {
            backgroundImage: `url(${Background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
            height: '100vh',
            overflow: 'hidden',
          },
        }}
      />

      <Box
        ref={heroRef as any}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          pb: { xs: 2, sm: 3, md: 4 },
          backdropFilter: "blur(2px)",
          backgroundColor: alpha('#ffffffff', 0.8),
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          overflow: 'hidden',
        }}
      >
        {/* SoundCloud iframe player - hidden but used for audio playback */}
        {/* Using iframe embed for better Safari compatibility */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "1px",
            height: "1px",
            opacity: 0,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <iframe
            ref={iframeRef}
            id="soundcloud-player"
            width="100%"
            height="166"
            scrolling="no"
            style={{ border: 'none' }}
            allow="autoplay"
            src="https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/eggnarok/youngji-lee-small-girl-feat-do-eggnarok-remix&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false"
          />
        </Box>

        {/* COUPLE GIF + centered overlayed play/pause button (responsive)
            NOTE: GIF size restored to original small height; overlay remains centered and smaller.
        */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            mt: { xs: '2vh', sm: '3vh', md: '2vh' },
          }}
        >
          {/* wrapper that is position:relative to place the IconButton centered over the GIF */}
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              // revert to original height-based sizing to keep GIF small
              height: { xs: '9vh', md: '14vh' },
              width: "auto",
            }}
          >
            <Box
              component="img"
              src={couple}
              alt="couple"
              sx={{
                zIndex: 100,
                position: "relative",
                height: { xs: '9vh', md: '14vh' }, // original sizes
                width: "auto",
                objectFit: "contain",
                display: "block",
                borderRadius: 2,
              }}
            />



            
            <IconButton
              aria-label={playerPlaying ? "pause" : "play"}
              onClick={() => setPlayerPlaying(!playerPlaying)}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 500,
                backgroundColor: 'rgba(0,0,0,0.45)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
              }}
              size="small"
            >
              {playerPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* logo and rest of hero content */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: { xs: '2vh', sm: '3vh', md: '2vh' } }}>
          <Box>
            <Box
              component="img"
              src={matejAndYun}
              alt="Matej and Yun"
              sx={{
                width: '100%',
                maxWidth: { xs: 900, sm: 1450, md: 2000 },
                height: { xs: '40vh', sm: '52vh', md: '60vh' },
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        </Box>

        <Link
          variant="h1"
          letterSpacing={15}
          fontFamily={"FUTURAM"}
          href="https://calendar.google.com/calendar/u/0/r/eventedit/MjVtYTRyNXBqOWYwNWVwajB1bzUyNGJhNHMgYm9yb3NtYXRlakBt"
          underline="none"
          sx={{
            mb: { xs: 0.5, sm: 0.75, md: 1 },
            fontSize: { xs: 32, sm: 42, md: 42 },
            textAlign: "center",
            color: "primary.contrastText",
            mt: { xs: '0vh', sm: '0.25vh', md: '-3vh' },
          }}
        >
          30.05.2026
        </Link>

        <Typography variant="h6" fontFamily={"FUTURAM"} sx={{ textAlign: "center", color: "primary.contrastText", mb: { xs: 0.25, sm: 0.5 } }}>
          {T("catedral_name")}
        </Typography>

        <Box sx={{ justifyContent: "center", alignItems: "center", display: "flex", flexDirection: "row", flexWrap: "wrap", mb: { xs: -0.5, sm: -1 } }}>
          {NavBarButtons()}
        </Box>

        <Box sx={{ 
          justifyContent: "center", 
          alignItems: "center", 
          display: "flex", 
          flexDirection: "row", 
          flexWrap: "nowrap", 
          mt: { xs: 0, sm: 0, md: 0 },
          mb: { xs: 4, sm: 3, md: 4 },
          gap: 1,
           width: "100%", maxWidth: "100vw",
          px: { xs: 1, sm: 2 },
          overflowX: "auto",
       //     '& > *': { flexShrink: 0 },  
        }}>
          {flagButtons()}
        </Box>
      </Box>

      <Section {...sections.details} innerRef={detailsRef}>
        <Typography variant="body1" component="h1" sx={{ mb: { xs: 2, sm: 3, md: 4 }, textAlign: "left", color: "secondary.main" }}>
          {T("intro_description")}
        </Typography>
        <Box sx={{ mt: { xs: 4, sm: 6, md: 8 }, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', width: '100%' }}>
          <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '90%', md: '100%' } }}>
            <MultiCarousel photos={usPhotos} autoPlay interval={2000} transitionTime={0} />
          </Box>
        </Box>
      </Section>

      <Section {...sections.program} innerRef={programRef}>
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ flex: 1 }}>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 2 } }}>
            <Typography variant="h5" sx={{ textAlign: 'left', color: 'primary.contrastText' }}>
              {T("program_update_notice")}
            </Typography>
            
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <CustomizedTimeline />
          </Grid>
        </Grid>
      </Section>

      <Section {...sections.location} innerRef={locationRef}>
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ flex: 1 }}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Typography variant="body1" component="h1" sx={{ mb: { xs: 1, sm: 1.5, md: 2 }, textAlign: "left", color: "secondary.main" }}>
              <Markdown>{T("location.description")}</Markdown>
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 12 }}>
            <img src={location} style={{ zIndex: 100, width: "100%" }} />
          </Grid>

          <Grid size={{ xs: 12, md: 12 }}>
            <BodyTypography text={T(`location.to_navigation`)} sx={{ marginTop: 0 }} />
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "left", gap: { xs: 0.5, sm: 1 }, whiteSpace: "nowrap", paddingTop: 0 }}>
              <Fab variant="extended" size="small" color="secondary" href="https://maps.app.goo.gl/Roh3hqBn7yvYhxKf9">
                <PlaceIcon />
                {T(`catedral_name`)}
              </Fab>
            </Box>
          </Grid>
        </Grid>
      </Section>

      <Section {...sections.dressCode} innerRef={dressCodeRef}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.5, sm: 1, md: 1.5 },
            alignItems: 'center',
            justifyContent: 'flex-start',
            py: { xs: 0.5, sm: 1, md: 1.5 },
            boxSizing: 'border-box',
          }}
        >
          <Typography variant="body1" component="div" sx={{ textAlign: "left", color: "primary.contrastText", px: { xs: 1, sm: 2 }, mt: { xs: 0.5, sm: 1 } }}>
            <Markdown>{T("dressCodeDescription")}</Markdown>
          </Typography>

          <Box
            sx={{
              width: { xs: '100%', sm: '92%', md: '86%' },
              height: { xs: 'calc(100vh - 260px)', sm: 'calc(100vh - 230px)', md: 'calc(100vh - 200px)' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: '120%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transform: { xs: 'scale(0.84)', sm: 'scale(0.90)', md: 'scale(0.96)' },
                transformOrigin: 'center top',
                '& > *': { width: '100%', height: '100%', maxHeight: '100%', maxWidth: '100%' },
              }}
            >
              <DressCodeMosaic colors={colorPalette} />
            </Box>
          </Box>
        </Box>
      </Section>

      <Section {...sections.questionsAndAnswers} innerRef={qnaRef}>
        <Box sx={{ pb: { xs: 1, sm: 2, md: 4 } }}>
          <FAQAccordion />
        </Box>
      </Section>

      <Box
        ref={footerRef as any}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          pb: { xs: 2, sm: 3, md: 4 },
          backdropFilter: "blur(2px)",
          backgroundColor: alpha('#ffffffff', 0.8),
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          overflow: 'hidden',
        }}
      >
        <Typography 
          variant="h3" 
          fontFamily="FUTURAM"
          onClick={() => heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          sx={{ 
            textAlign: "center", 
            color: "primary.contrastText",
            cursor: 'pointer',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
            px: { xs: 2, sm: 3, md: 4 },
            '&:hover': {
              textDecoration: 'underline',
            }
          }}
        >
          {T("seeYouAtParty")}
        </Typography>
      </Box>
    </Box>
  );
}